import { useState, useEffect } from 'react';
import { useSettings } from '../hooks/useSettings';
import { usePortfolio } from '../hooks/usePortfolio';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Settings as SettingsIcon, DollarSign, Moon, Sun, RefreshCw, Trash2, AlertTriangle, Save, CheckCircle } from 'lucide-react';

// Force HMR update on file change
if (import.meta.hot) {
  import.meta.hot.accept();
}

export default function Settings() {
  const { settings, updateSettings, resetSettings } = useSettings();
  const { portfolio, clearPortfolio } = usePortfolio();
  
  // Local state for settings (not saved until Save button is clicked)
  const [localSettings, setLocalSettings] = useState(settings);
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local settings when global settings change
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Check if there are unsaved changes
  useEffect(() => {
    const hasUnsavedChanges = JSON.stringify(localSettings) !== JSON.stringify(settings);
    setHasChanges(hasUnsavedChanges);
  }, [localSettings, settings]);

  const handleCurrencyChange = (currency: 'usd' | 'eur' | 'gbp' | 'jpy' | 'mxn' | 'php') => {
    setLocalSettings((prev) => ({ ...prev, currency }));
  };

  const handleThemeChange = (theme: 'light' | 'dark' | 'auto') => {
    setLocalSettings((prev) => ({ ...prev, theme }));
  };

  const handleRefreshIntervalChange = (interval: number) => {
    setLocalSettings((prev) => ({ ...prev, refreshInterval: interval }));
  };

  const handleShowPortfolioChange = (show: boolean) => {
    setLocalSettings((prev) => ({ ...prev, showPortfolio: show }));
  };

  const handleSave = () => {
    updateSettings(localSettings);
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
      // Reload the page to apply changes
      window.location.reload();
    }, 1000);
  };

  const handleCancel = () => {
    setLocalSettings(settings);
    setHasChanges(false);
  };

  const handleClearPortfolio = () => {
    if (confirm('Are you sure you want to clear your entire portfolio? This cannot be undone.')) {
      clearPortfolio();
    }
  };

  const handleResetSettings = () => {
    if (confirm('Are you sure you want to reset all settings to default?')) {
      resetSettings();
      setShowSaveIndicator(true);
      setTimeout(() => setShowSaveIndicator(false), 2000);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <SettingsIcon className="h-8 w-8 text-black dark:text-white" />
          <div>
            <h1 className="text-2xl font-bold text-black dark:text-white">Settings</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              Customize your BlockPeek experience
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showSaveIndicator && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg animate-fade-in">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">
                Saved!
              </span>
            </div>
          )}
          {hasChanges && (
            <>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="font-semibold text-black dark:text-white flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Currency
            </h3>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { value: 'usd', label: 'US Dollar', symbol: '$' },
              { value: 'eur', label: 'Euro', symbol: '€' },
              { value: 'gbp', label: 'British Pound', symbol: '£' },
              { value: 'jpy', label: 'Japanese Yen', symbol: '¥' },
              { value: 'mxn', label: 'Mexican Peso', symbol: '$' },
              { value: 'php', label: 'Philippine Peso', symbol: '₱' },
            ].map((curr) => (
              <button
                key={curr.value}
                onClick={() => handleCurrencyChange(curr.value as any)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                  localSettings.currency === curr.value
                    ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="font-medium text-black dark:text-white">
                  {curr.label}
                </span>
                <Badge variant={localSettings.currency === curr.value ? 'default' : 'outline'}>
                  {curr.symbol}
                </Badge>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="font-semibold text-black dark:text-white flex items-center gap-2">
              <Moon className="h-5 w-5" />
              Theme
            </h3>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { value: 'light', label: 'Light', icon: Sun },
              { value: 'dark', label: 'Dark', icon: Moon },
              { value: 'auto', label: 'Auto (System)', icon: RefreshCw },
            ].map((theme) => (
              <button
                key={theme.value}
                onClick={() => handleThemeChange(theme.value as any)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                  localSettings.theme === theme.value
                    ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="font-medium text-black dark:text-white">
                  {theme.label}
                </span>
                <theme.icon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="font-semibold text-black dark:text-white flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Refresh Interval
            </h3>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { value: 15, label: '15 seconds' },
              { value: 30, label: '30 seconds' },
              { value: 60, label: '1 minute' },
              { value: 300, label: '5 minutes' },
            ].map((interval) => (
              <button
                key={interval.value}
                onClick={() => handleRefreshIntervalChange(interval.value)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                  localSettings.refreshInterval === interval.value
                    ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="font-medium text-black dark:text-white">
                  {interval.label}
                </span>
                {localSettings.refreshInterval === interval.value && (
                  <Badge variant="default">Active</Badge>
                )}
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="font-semibold text-black dark:text-white flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Portfolio Visibility
            </h3>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div>
                <p className="font-medium text-black dark:text-white">Show Portfolio</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Display portfolio tab in navigation
                </p>
              </div>
              <button
                onClick={() => handleShowPortfolioChange(!localSettings.showPortfolio)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  localSettings.showPortfolio
                    ? 'bg-black dark:bg-white'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localSettings.showPortfolio ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="font-semibold text-black dark:text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              Danger Zone
            </h3>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div>
                <p className="font-medium text-black dark:text-white">Clear Portfolio</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Remove all assets from your portfolio
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleClearPortfolio}
                disabled={portfolio.length === 0}
              >
                <Trash2 className="h-4 w-4" />
                Clear
              </Button>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div>
                <p className="font-medium text-black dark:text-white">Reset Settings</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Restore all settings to default
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetSettings}
              >
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">About BlockPeek</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
            <p>
              <strong>Version:</strong> 1.0.0
            </p>
            <p>
              <strong>API:</strong> Powered by CoinGecko
            </p>
            <p>
              <strong>Features:</strong> Real-time price tracking, portfolio management, historical
              charts, and customizable settings.
            </p>
            <p>
              <strong>Privacy:</strong> All your data is stored locally in your browser. No data is
              sent to external servers except for fetching cryptocurrency prices.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}